import { MarketHistoriesResponse, MarketResponse } from './ao-data-interfaces';
import { CraftResource, CraftingRequirements, Item } from './items.model';
import { Observable, Subscription } from 'rxjs';
import { PriceType, SalesTax, SetupFee } from './market.model';
import { FAME_BONUS_PREMIUM, PRICE_TYPES, RRR, SALES_TAXES, SETUP_FEES, getPriceTypeByName, getSetupFeeByName } from '../constants/market.constant';
import { LOCATIONS, getLocation } from '../constants/locations.constant';
import { JsonReaderService } from 'src/app/services/json-reader/json-reader.service';
import { AoDataGetterService } from 'src/app/services/ao-data-getter/ao-data-getter.service';
import { Location } from './location.model';
import { Achievement } from './achievement.model';
import { findMatchingAchievements, getFocusEfficiency, getFocusEfficiencyText, matchesPattern } from '../functions/achievements-functions';
import { UserSettingsService } from 'src/app/services/user-settings/user-settings.service';
import { DocumentData, FirestoreDataConverter, QueryDocumentSnapshot, Timestamp } from 'firebase/firestore/lite';

export class DbCraft {
  craftId?: string;
  productUniqueName!: string;
  amountCrafted!: number;
  totalCost!: number;
  totalIncome!: number;
  totalCraftingFee!: number;
  totalFocus!: number;
  ingredients!: { uniqueName: string; totalCost: number; amount: number; leftOverResult: number; leftOverAmount: number }[];
  totalFame!: number;
  journalAmount!: number;
  emptyJournal!: { uniqueName: string; totalCost: number };
  fullJournal!: { uniqueName: string; totalIncome: number };
  craftingConfiguration!: CraftConfiguration;
  server: string;
  date: Date;
  craftFood!: {
    uniqueName: string | null;
    amount: number;
    totalCost: number;
  };

  transportFood!: {
    uniqueName: string | null;
    amount: number;
    totalCost: number;
  };

  constructor(recipe?: Recipe, server?: string) {
    if (recipe) {
      this.productUniqueName = recipe.craft.product.uniqueName;
      this.amountCrafted = recipe.productProducedAmountAllRuns;
      this.totalCost = recipe.totalCostAllRuns;
      this.totalIncome = recipe.totalIncomeAllRuns;
      this.totalCraftingFee = recipe.craftingFeeAllRuns;
      this.totalFocus = recipe.focusAllRuns;
      this.ingredients = recipe.ingredients.map((x) => {
        return {
          uniqueName: x.ingredient?.uniqueName ?? '',
          totalCost: x.totalCostAllRuns,
          amount: x.totalAmountAfterRRRAllRuns,
          leftOverAmount: x.leftoverAmountReal ?? 0,
          leftOverResult: x.leftOverResultAllRuns ?? 0,
        };
      });
      this.totalFame = recipe.actualFamePlayerAllRuns;
      this.journalAmount = recipe.numberOfFilledJournalsAllRuns;
      this.emptyJournal = {
        uniqueName: recipe.craft.journalEmpty?.uniqueName ?? '',
        totalCost: recipe.journalEmptyBuyAllRuns + recipe.journalEmptyBuySetupFeeAllRuns,
      };
      this.fullJournal = {
        uniqueName: recipe.craft.journalFull?.uniqueName ?? '',
        totalIncome: recipe.journalFullSellAllRuns - recipe.journalFullSalesTaxAllRuns - recipe.journalFullSellSetupFeeAllRuns,
      };
      this.craftingConfiguration = recipe.craft.craftConfig;
      this.craftFood = {
        uniqueName: this.craftingConfiguration.getCraftFood(recipe.craft.jsonReaderService)?.uniqueName ?? null,
        amount: this.craftingConfiguration.craftFoodAmount,
        totalCost: recipe.totalCraftFoodCost,
      };
      this.transportFood = {
        uniqueName: this.craftingConfiguration.getTransportFood(recipe.craft.jsonReaderService)?.uniqueName ?? null,
        amount: this.craftingConfiguration.transportFoodAmount,
        totalCost: recipe.totalTransportFoodCost,
      };
    }

    this.server = server ?? '';
    this.date = new Date();
  }

  get result(): number {
    return this.totalIncome - this.totalCost;
  }

  get resultPercentage(): number {
    return this.result / this.totalCost;
  }

  get resultPerFocus(): number {
    return this.result / this.totalFocus;
  }

  get ingredientsLeftOverResult(): number {
    return this.ingredients.reduce((sum, ingredient) => sum + ingredient.leftOverResult, 0);
  }

  toFirebaseObject(): any {
    return {
      productUniqueName: this.productUniqueName,
      amountCrafted: this.amountCrafted,
      totalCost: this.totalCost,
      totalIncome: this.totalIncome,
      totalCraftingFee: this.totalCraftingFee,
      totalFocus: this.totalFocus,
      ingredients: this.ingredients,
      totalFame: this.totalFame,
      journalAmount: this.journalAmount,
      emptyJournal: this.emptyJournal,
      fullJournal: this.fullJournal,
      craftingConfiguration: JSON.parse(JSON.stringify(this.craftingConfiguration)),
      craftFood: this.craftFood,
      transportFood: this.transportFood,
      server: this.server,
      date: Timestamp.fromDate(this.date),
    };
  }

  static firebaseObjectToDbCraft(firebaseObject: any): DbCraft {
    const dbCraft = new DbCraft();

    dbCraft.productUniqueName = firebaseObject.productUniqueName;
    dbCraft.amountCrafted = firebaseObject.amountCrafted;
    dbCraft.totalCost = firebaseObject.totalCost;
    dbCraft.totalIncome = firebaseObject.totalIncome;
    dbCraft.totalCraftingFee = firebaseObject.totalCraftingFee;
    dbCraft.totalFocus = firebaseObject.totalFocus;
    dbCraft.ingredients = firebaseObject.ingredients.map((x: any) => {
      return {
        uniqueName: x.uniqueName,
        totalCost: x.totalCost,
        amount: x.amount,
        leftOverAmount: x.leftOverAmount ?? 0,
        leftOverResult: x.leftOverResult ?? 0,
      };
    });
    dbCraft.totalFame = firebaseObject.totalFame;
    dbCraft.journalAmount = firebaseObject.journalAmount;
    dbCraft.emptyJournal = firebaseObject.emptyJournal;
    dbCraft.fullJournal = firebaseObject.fullJournal;
    dbCraft.craftingConfiguration = CraftConfiguration.fromJson(JSON.stringify(firebaseObject.craftingConfiguration));
    dbCraft.craftFood = firebaseObject.craftFood ?? {
      uniqueName: null,
      amount: 0,
      totalCost: 0,
    };
    dbCraft.transportFood = firebaseObject.transportFood ?? {
      uniqueName: null,
      amount: 0,
      totalCost: 0,
    };
    dbCraft.date = (firebaseObject.date as Timestamp).toDate();
    dbCraft.server = firebaseObject.server;

    return dbCraft;
  }
}

export const dbCraftConverter: FirestoreDataConverter<DbCraft> = {
  toFirestore(craft: DbCraft): DocumentData {
    return craft.toFirebaseObject();
  },
  fromFirestore(snapshot: QueryDocumentSnapshot): DbCraft {
    const firebaseObject = snapshot.data();
    const dbCraft = new DbCraft();

    dbCraft.craftId = snapshot.id;
    dbCraft.productUniqueName = firebaseObject['productUniqueName'];
    dbCraft.amountCrafted = firebaseObject['amountCrafted'];
    dbCraft.totalCost = firebaseObject['totalCost'];
    dbCraft.totalIncome = firebaseObject['totalIncome'];
    dbCraft.totalCraftingFee = firebaseObject['totalCraftingFee'];
    dbCraft.totalFocus = firebaseObject['totalFocus'];
    dbCraft.ingredients = firebaseObject['ingredients'];
    dbCraft.totalFame = firebaseObject['totalFame'];
    dbCraft.journalAmount = firebaseObject['journalAmount'];
    dbCraft.emptyJournal = firebaseObject['emptyJournal'];
    dbCraft.fullJournal = firebaseObject['fullJournal'];
    dbCraft.craftingConfiguration = CraftConfiguration.fromJson(JSON.stringify(firebaseObject['craftingConfiguration']));
    dbCraft.date = (firebaseObject['date'] as Timestamp).toDate();
    dbCraft.server = firebaseObject['server'];

    return dbCraft;
  },
};

export class Craft {
  userSettingsService: UserSettingsService;
  jsonReaderService: JsonReaderService;
  aoDataGetterService: AoDataGetterService;
  product: Item;
  productAchievements: Achievement[] = [];
  journalEmpty?: Item;
  journalFull?: Item;
  recipes: Recipe[] = [];
  craftConfig: CraftConfiguration;
  prices$: Observable<MarketResponse[] | null>;
  prices: MarketResponse[] = [];
  histories$: Observable<MarketHistoriesResponse[] | null>;
  histories: MarketHistoriesResponse[] = [];
  foodPrices$: Observable<MarketResponse[] | null>;
  foodPrices: MarketResponse[] = [];
  foodHistories$: Observable<MarketHistoriesResponse[] | null>;
  foodHistories: MarketHistoriesResponse[] = [];

  get hasNoMissingPrices(): boolean {
    for (let recipe of this.recipes) {
      if (!recipe.hasNoMissingPrices) {
        return false;
      }
    }
    return true;
  }

  get bestRecipe(): Recipe {
    let bestRecipe: Recipe = this.recipes[0];

    if (this.craftConfig.onlymainRecipe) return bestRecipe; // If we only want the main recipe, return it

    let highestProfit = -Infinity; // Start with the lowest possible number

    for (const recipe of this.recipes.filter((x) => x.hasNoMissingPrices)) {
      const profit = recipe.totalProfitAllRuns; // Calculate the total profit for the recipe
      if (profit > highestProfit) {
        // Check if this recipe's profit is higher than the current highest
        highestProfit = profit;
        bestRecipe = recipe;
      }
    }
    return bestRecipe; // Return the recipe with the highest profit
  }

  //#region Product Unit Prices
  productSellRealPriceUnit: number = 0;
  get productMinMaxPrice(): {
    minSellPrice: MarketResponse | undefined;
    maxBuyPrice: MarketResponse | undefined;
  } {
    return this.aoDataGetterService.getMinAndMaxPrice(this.product, this.craftConfig.sellLocation, this.prices, undefined);
  }
  get productAverage(): { averagePrice: number | undefined; averageAmount: number | undefined } {
    return this.aoDataGetterService.getAveragePriceAndAmount(
      this.product,
      this.craftConfig.sellLocation,
      this.histories,
      undefined,
      this.craftConfig.averageNumberOfDays
    );
  }
  get productSellPriceUnit(): number {
    switch (this.craftConfig.sellPriceType) {
      case PRICE_TYPES.average: {
        return this.productAverage.averagePrice ?? 0;
      }
      case PRICE_TYPES.buy: {
        return this.productMinMaxPrice.maxBuyPrice?.buyPriceMax ?? 0;
      }
      case PRICE_TYPES.sell: {
        return this.productMinMaxPrice.minSellPrice?.sellPriceMin ?? 0;
      }
      default: {
        return 0;
      }
    }
  }

  updateProductSellRealPriceUnit() {
    this.productSellRealPriceUnit = Math.round(this.productSellPriceUnit);
  }
  //#endregion

  //#region Journal Unit Prices
  get hasJournal() {
    return this.journalEmpty && this.journalFull;
  }
  journalEmptyBuyRealPriceUnit: number = 0;
  journalFullSellRealPriceUnit: number = 0;
  get journalEmptyMinMaxPrice(): {
    minSellPrice: MarketResponse | undefined;
    maxBuyPrice: MarketResponse | undefined;
  } {
    return this.aoDataGetterService.getMinAndMaxPrice(this.journalEmpty, this.craftConfig.journalBuyLocation, this.prices, undefined);
  }
  get journalFullMinMaxPrice(): {
    minSellPrice: MarketResponse | undefined;
    maxBuyPrice: MarketResponse | undefined;
  } {
    return this.aoDataGetterService.getMinAndMaxPrice(this.journalFull, this.craftConfig.journalSellLocation, this.prices, undefined);
  }
  get journalEmptyAverage(): { averagePrice: number | undefined; averageAmount: number | undefined } {
    return this.aoDataGetterService.getAveragePriceAndAmount(
      this.journalEmpty,
      this.craftConfig.journalBuyLocation,
      this.histories,
      undefined,
      this.craftConfig.averageNumberOfDays
    );
  }
  get journalFullAverage(): { averagePrice: number | undefined; averageAmount: number | undefined } {
    return this.aoDataGetterService.getAveragePriceAndAmount(
      this.journalFull,
      this.craftConfig.journalSellLocation,
      this.histories,
      undefined,
      this.craftConfig.averageNumberOfDays
    );
  }
  get journalFullSellPriceUnit(): number {
    switch (this.craftConfig.journalSellPriceType) {
      case PRICE_TYPES.average: {
        return this.journalFullAverage.averagePrice ?? 0;
      }
      case PRICE_TYPES.buy: {
        return this.journalFullMinMaxPrice.maxBuyPrice?.buyPriceMax ?? 0;
      }
      case PRICE_TYPES.sell: {
        return this.journalFullMinMaxPrice.minSellPrice?.sellPriceMin ?? 0;
      }
      default: {
        return 0;
      }
    }
  }
  updateJournalFullSellRealPriceUnit() {
    this.journalFullSellRealPriceUnit = Math.round(this.journalFullSellPriceUnit);
  }
  get journalEmptyBuyPriceUnit(): number {
    switch (this.craftConfig.journalBuyPriceType) {
      case PRICE_TYPES.average: {
        return this.journalEmptyAverage.averagePrice ?? 0;
      }
      case PRICE_TYPES.buy: {
        return this.journalEmptyMinMaxPrice.maxBuyPrice?.buyPriceMax ?? 0;
      }
      case PRICE_TYPES.sell: {
        return this.journalEmptyMinMaxPrice.minSellPrice?.sellPriceMin ?? 0;
      }
      default: {
        return 0;
      }
    }
  }
  updateJournalEmptyBuyRealPriceUnit() {
    this.journalEmptyBuyRealPriceUnit = Math.round(this.journalEmptyBuyPriceUnit);
  }
  //#endregion

  //#region Food Unit Prices
  craftFoodBuyRealPriceUnit: number = 0;
  transportFoodBuyRealPriceUnit: number = 0;
  get craftFoodMinMaxPrice(): {
    minSellPrice: MarketResponse | undefined;
    maxBuyPrice: MarketResponse | undefined;
  } {
    const item = this.craftConfig.getCraftFood(this.jsonReaderService);
    if (item) {
      return this.aoDataGetterService.getMinAndMaxPrice(item, this.craftConfig.buyLocation, this.foodPrices, undefined);
    }
    return { minSellPrice: undefined, maxBuyPrice: undefined };
  }

  get transportFoodMinMaxPrice(): {
    minSellPrice: MarketResponse | undefined;
    maxBuyPrice: MarketResponse | undefined;
  } {
    const item = this.craftConfig.getTransportFood(this.jsonReaderService);
    if (item) {
      return this.aoDataGetterService.getMinAndMaxPrice(item, this.craftConfig.buyLocation, this.foodPrices, undefined);
    }
    return { minSellPrice: undefined, maxBuyPrice: undefined };
  }

  get craftFoodAverage(): { averagePrice: number | undefined; averageAmount: number | undefined } {
    const item = this.craftConfig.getCraftFood(this.jsonReaderService);
    if (item) {
      return this.aoDataGetterService.getAveragePriceAndAmount(
        item,
        this.craftConfig.buyLocation,
        this.foodHistories,
        undefined,
        this.craftConfig.averageNumberOfDays
      );
    }
    return { averagePrice: undefined, averageAmount: undefined };
  }

  get transportFoodAverage(): { averagePrice: number | undefined; averageAmount: number | undefined } {
    const item = this.craftConfig.getTransportFood(this.jsonReaderService);
    if (item) {
      return this.aoDataGetterService.getAveragePriceAndAmount(
        item,
        this.craftConfig.buyLocation,
        this.foodHistories,
        undefined,
        this.craftConfig.averageNumberOfDays
      );
    }
    return { averagePrice: undefined, averageAmount: undefined };
  }

  get craftFoodBuyPriceUnit(): number {
    switch (this.craftConfig.foodBuyPriceType) {
      case PRICE_TYPES.average: {
        return this.craftFoodAverage.averagePrice ?? 0;
      }
      case PRICE_TYPES.buy: {
        return this.craftFoodMinMaxPrice.maxBuyPrice?.buyPriceMax ?? 0;
      }
      case PRICE_TYPES.sell: {
        return this.craftFoodMinMaxPrice.minSellPrice?.sellPriceMin ?? 0;
      }
      default: {
        return 0;
      }
    }
  }

  get transportFoodBuyPriceUnit(): number {
    switch (this.craftConfig.foodBuyPriceType) {
      case PRICE_TYPES.average: {
        return this.transportFoodAverage.averagePrice ?? 0;
      }
      case PRICE_TYPES.buy: {
        return this.transportFoodMinMaxPrice.maxBuyPrice?.buyPriceMax ?? 0;
      }
      case PRICE_TYPES.sell: {
        return this.transportFoodMinMaxPrice.minSellPrice?.sellPriceMin ?? 0;
      }
      default: {
        return 0;
      }
    }
  }

  updateFoodsBuyRealPriceUnit() {
    this.craftFoodBuyRealPriceUnit = Math.round(this.craftFoodBuyPriceUnit);
    this.transportFoodBuyRealPriceUnit = Math.round(this.transportFoodBuyPriceUnit);
  }
  //#endregion

  //#region Ingredients

  updateIngredientsBuyPriceRealUnit() {
    this.recipes
      .flatMap((x) => x.ingredients)
      .map((x) => {
        x.buyPriceRealUnit = Math.round(x.buyPriceUnit);
      });
  }

  //#endregion

  updatePriceRealUnitForAverages() {
    //ingredients
    if (this.craftConfig.buyPriceType === PRICE_TYPES.average) {
      this.updateIngredientsBuyPriceRealUnit();
    }
    //product
    if (this.craftConfig.sellPriceType === PRICE_TYPES.average) {
      this.updateProductSellRealPriceUnit();
    }
    //empty journal
    if (this.craftConfig.journalBuyPriceType === PRICE_TYPES.average) {
      this.updateJournalEmptyBuyRealPriceUnit();
    }
    //full journal
    if (this.craftConfig.journalSellPriceType === PRICE_TYPES.average) {
      this.updateJournalFullSellRealPriceUnit();
    }
    //food
    if (this.craftConfig.foodBuyPriceType === PRICE_TYPES.average) {
      this.updateFoodsBuyRealPriceUnit();
    }
  }

  constructor(
    _userSettingsService: UserSettingsService,
    _jsonReaderService: JsonReaderService,
    _aoDataGetterService: AoDataGetterService,
    _product: Item,
    _crafConfig: CraftConfiguration,
    _prices$: Observable<MarketResponse[] | null>,
    _histories$: Observable<MarketHistoriesResponse[] | null>,
    _foodPrices$: Observable<MarketResponse[] | null>,
    _foodHistories$: Observable<MarketHistoriesResponse[] | null>,
    private subscriptions: Subscription[]
  ) {
    this.userSettingsService = _userSettingsService;
    this.jsonReaderService = _jsonReaderService;
    this.aoDataGetterService = _aoDataGetterService;
    this.product = _product;
    this.craftConfig = _crafConfig;
    this.prices$ = _prices$;
    this.histories$ = _histories$;
    this.foodPrices$ = _foodPrices$;
    this.foodHistories$ = _foodHistories$;

    this.initialSetup();
  }

  private initialSetup() {
    this.setJournals();

    this.subscriptions.push(
      this.prices$.subscribe((prices) => {
        if (prices) {
          this.prices = prices;
          this.updateIngredientsBuyPriceRealUnit();
          this.updateProductSellRealPriceUnit();
          this.updateJournalEmptyBuyRealPriceUnit();
          this.updateJournalFullSellRealPriceUnit();
        }
      }),

      this.foodPrices$.subscribe((prices) => {
        if (prices) {
          this.foodPrices = prices;
          this.updateFoodsBuyRealPriceUnit();
        }
      }),
      this.histories$.subscribe((histories) => {
        if (histories) {
          this.histories = histories;
          this.updateIngredientsBuyPriceRealUnit();
          this.updateProductSellRealPriceUnit();
          this.updateJournalEmptyBuyRealPriceUnit();
          this.updateJournalFullSellRealPriceUnit();
        }
      }),

      this.foodHistories$.subscribe((histories) => {
        if (histories) {
          this.foodHistories = histories;
          this.updateFoodsBuyRealPriceUnit();
        }
      })
    );
    this.setRecipes();
  }

  setProductAchievements(achievements: Achievement[], specMap: Map<string, number>) {
    if (achievements) {
      this.productAchievements = findMatchingAchievements(achievements, this.product, 'craftitemfame');
      for (let ach of this.productAchievements) {
        ach.playerLevel = specMap.get(ach.id) ?? 0;
      }
    }
  }

  setRecipes() {
    this.recipes = [];
    for (let recipe of this.product.craftingRequirements) {
      this.recipes.push(new Recipe(this, recipe));
    }
  }

  get focusEfficiency(): number {
    return getFocusEfficiency(this.productAchievements, this.product, 'craftingfocuscostreduction');
  }

  getFocusEfficiencyText(ach: Achievement): string {
    return getFocusEfficiencyText(ach, this.product, 'craftingfocuscostreduction');
  }

  private setJournals() {
    let journals = this.jsonReaderService.getJournalByItem(this.product);
    this.journalEmpty = journals.empty;
    this.journalFull = journals.full;
  }
}

export class CraftConfiguration {
  rrr: number = RRR[2];
  useManualRRR: boolean = false;
  averageNumberOfDays: number = 7;
  recipeRuns: number = 10;
  isPremium: boolean = true;
  usedFocus: boolean = false;
  usedJournals: boolean = true;
  usedFood: boolean = false;
  onlymainRecipe: boolean = false;
  feeNutrition: number = 300;
  buyPriceType: PriceType = PRICE_TYPES.sell;
  sellPriceType: PriceType = PRICE_TYPES.average;
  setupFee: SetupFee = SETUP_FEES.normal;
  buyLocation: Location = LOCATIONS.Caerleon;
  sellLocation: Location = LOCATIONS.BlackMarket;
  journalBuyLocation: Location = LOCATIONS.Caerleon;
  journalSellLocation: Location = LOCATIONS.Caerleon;
  journalBuyPriceType: PriceType = PRICE_TYPES.sell;
  journalSellPriceType: PriceType = PRICE_TYPES.buy;
  foodBuyPriceType: PriceType = PRICE_TYPES.sell;
  private craftFoodUniqueName: string | null = null;
  craftFoodAmount: number = 1;
  private transportFoodUniqueName: string | null = null;
  transportFoodAmount: number = 1;

  constructor() {}

  get salesTax(): SalesTax {
    if (this.isPremium) {
      return SALES_TAXES.premium;
    } else {
      return SALES_TAXES.normal;
    }
  }

  getCraftFood(jsonReaderService: JsonReaderService): Item | null {
    if (!this.craftFoodUniqueName) return null;
    return jsonReaderService.getItemFromIndex(this.craftFoodUniqueName) ?? null;
  }

  setCraftFood(item: Item | null) {
    if (item) {
      this.craftFoodUniqueName = item.uniqueName;
    } else {
      this.craftFoodUniqueName = null;
    }
  }

  getTransportFood(jsonReaderService: JsonReaderService): Item | null {
    if (!this.transportFoodUniqueName) return null;
    return jsonReaderService.getItemFromIndex(this.transportFoodUniqueName) ?? null;
  }

  setTransportFood(item: Item | null) {
    if (item) {
      this.transportFoodUniqueName = item.uniqueName;
    } else {
      this.transportFoodUniqueName = null;
    }
  }

  static fromJson(json: string): CraftConfiguration {
    const plainObject = JSON.parse(json);

    const result = Object.assign(new CraftConfiguration(), plainObject);

    result.buyLocation = getLocation(Number(result.buyLocation.index));
    result.sellLocation = getLocation(Number(result.sellLocation.index));
    result.journalBuyLocation = getLocation(Number(result.journalBuyLocation.index));
    result.journalSellLocation = getLocation(Number(result.journalSellLocation.index));

    result.buyPriceType = getPriceTypeByName(result.buyPriceType.name);
    result.sellPriceType = getPriceTypeByName(result.sellPriceType.name);
    result.journalBuyPriceType = getPriceTypeByName(result.journalBuyPriceType.name);
    result.journalSellPriceType = getPriceTypeByName(result.journalSellPriceType.name);
    result.foodBuyPriceType = getPriceTypeByName(result.foodBuyPriceType.name);

    result.setupFee = getSetupFeeByName(result.setupFee.name);

    return result;
  }
}

export class Recipe {
  //#region Class Stuff
  craft: Craft;
  recipe: CraftingRequirements;
  ingredients: Ingredient[] = [];
  fame$!: Observable<number>;
  constructor(_craft: Craft, _recipe: CraftingRequirements) {
    this.craft = _craft;
    this.recipe = _recipe;

    for (let ing of this.recipe.craftResources) {
      this.ingredients.push(new Ingredient(this, ing));
    }
  }
  //#endregion

  get hasNoMissingPrices() {
    //journals
    let journalOk = true;
    if (this.craft.hasJournal && this.craft.craftConfig.usedJournals) {
      journalOk = this.craft.journalEmptyBuyRealPriceUnit > 0 && this.craft.journalFullSellRealPriceUnit > 0;
    }

    //food
    let foodOk = true;
    let craftFoodOk = true;
    let transportFoodOk = true;
    if (this.craft.craftConfig.usedFood) {
      if (this.craft.craftConfig.getCraftFood(this.craft.jsonReaderService)) {
        craftFoodOk = this.craft.craftFoodBuyRealPriceUnit > 0;
      }
      if (this.craft.craftConfig.getTransportFood(this.craft.jsonReaderService)) {
        transportFoodOk = this.craft.transportFoodBuyRealPriceUnit > 0;
      }
    }
    foodOk = craftFoodOk && transportFoodOk;

    //product
    let productOk = this.craft.productSellRealPriceUnit > 0;

    //ingredients
    let ingredientsOk = this.ingredients.map((x) => x.buyPriceRealUnit > 0).every((x) => x === true);

    return journalOk && foodOk && productOk && ingredientsOk;
  }
  //#region Fame
  get baseFameJournalPerRun(): number {
    //this fame is for non premium and is the JOURNAL fame
    return this.craft.jsonReaderService.getItemCraftFame(this.craft.product, this.recipe) ?? 0;
  }
  get baseFamePlayerPerRun(): number {
    return this.baseFameJournalPerRun * (this.craft.product.craftFameFactor ?? 1);
  }
  get actualFamePlayerPerRun(): number {
    let baseFame = this.baseFamePlayerPerRun;
    return this.craft.craftConfig.isPremium ? baseFame * (1 + FAME_BONUS_PREMIUM) : baseFame;
  }
  get baseFameJournalAllRuns(): number {
    return this.baseFameJournalPerRun * this.craft.craftConfig.recipeRuns;
  }
  get actualFamePlayerAllRuns(): number {
    return this.actualFamePlayerPerRun * this.craft.craftConfig.recipeRuns;
  }
  get famePerFocusAllRuns(): number {
    return this.actualFamePlayerAllRuns / this.focusAllRuns;
  }
  //#endregion

  //#region Crafting Fee

  get craftingFeeAllRuns(): number {
    return this.craft.jsonReaderService.getCraftingFee(this.craft.product, this.craft.craftConfig.feeNutrition) * this.productProducedAmountAllRuns;
  }

  //#endregion

  //#region Focus
  get focusPerRun(): number {
    return this.craft.jsonReaderService.getFocusToCraft(this.recipe, this.craft.focusEfficiency) * this.productProducedAmountPerRun;
  }

  get focusAllRuns(): number {
    return this.focusPerRun * this.craft.craftConfig.recipeRuns;
  }
  get profitPerFocusAllRuns(): number {
    return this.totalProfitAllRuns / this.focusAllRuns;
  }
  //#endregion

  //#region Product
  get productProducedAmountPerRun(): number {
    return this.recipe.amountCrafted ?? 1;
  }
  get productProducedAmountAllRuns(): number {
    return this.productProducedAmountPerRun * this.craft.craftConfig.recipeRuns;
  }
  get productSalesTaxPerRun(): number {
    return this.productSellPerRun * this.craft.craftConfig.salesTax.value;
  }
  get productSalesTaxAllRuns(): number {
    return Math.ceil(this.productSalesTaxPerRun * this.craft.craftConfig.recipeRuns);
  }
  get productSetupFeePerRun(): number {
    if ([PRICE_TYPES.sell, PRICE_TYPES.average].includes(this.craft.craftConfig.sellPriceType)) {
      return this.productSellPerRun * this.craft.craftConfig.setupFee.value;
    }
    return 0;
  }
  get productSetupFeeAllRuns(): number {
    return Math.ceil(this.productSetupFeePerRun * this.craft.craftConfig.recipeRuns);
  }
  get productCostPerRun(): number {
    return this.productSalesTaxPerRun + this.productSetupFeePerRun;
  }
  get productCostAllRuns(): number {
    return this.productCostPerRun * this.craft.craftConfig.recipeRuns;
  }
  get productSellPerRun(): number {
    return this.craft.productSellRealPriceUnit * (this.recipe.amountCrafted ?? 1);
  }
  get productSellAllRuns(): number {
    return this.productSellPerRun * this.craft.craftConfig.recipeRuns;
  }
  get productTotalWeight(): number {
    return this.productProducedAmountAllRuns * (this.craft.product.weight ?? 0);
  }
  //#endregion

  //#region Ingredients

  get ingredientsTotalWeight(): number {
    return this.ingredients.map((x) => x.totalWeight).reduce((sum, x) => sum + x, 0);
  }
  get ingredientsSetupFeeAllRuns(): number {
    let result = 0;
    for (let ing of this.ingredients) {
      result += ing.setupFeeCostAllRuns;
    }
    return result;
  }
  get ingredientsBuyCostAllRuns(): number {
    let result = 0;
    for (let ing of this.ingredients) {
      result += ing.buyPriceAllRuns;
    }
    return result;
  }
  get ingredientsTotalCostAllRuns(): number {
    let result = 0;
    for (let ing of this.ingredients) {
      result += ing.totalCostAllRuns;
    }
    return result;
  }

  get ingredientsLeftOverIncomeAllRuns(): number {
    let result = 0;
    for (let ing of this.ingredients) {
      result += ing.leftOverResultAllRuns;
    }
    return result;
  }
  //#endregion

  //#region Fixed Cost
  get recipeFixedCostPerRun(): number {
    return this.recipe.silver ?? 0;
  }
  get recipeFixedCostAllRuns(): number {
    return this.recipeFixedCostPerRun * this.craft.craftConfig.recipeRuns;
  }
  //#endregion

  //#region Cost
  get totalCostAllRuns(): number {
    // ADD INGREDIENTS COSTS
    return (
      this.totalJournalCostAllRuns +
      this.recipeFixedCostAllRuns +
      this.craftingFeeAllRuns +
      this.productCostAllRuns +
      this.ingredientsTotalCostAllRuns +
      this.totalFoodCost
    );
  }
  //#endregion

  //#region Income
  get totalIncomeAllRuns(): number {
    return this.journalFullSellAllRuns + this.productSellAllRuns + this.ingredientsLeftOverIncomeAllRuns;
  }
  //#endregion

  //#region Profit
  get totalProfitAllRuns(): number {
    return this.totalIncomeAllRuns - this.totalCostAllRuns;
  }
  //#endregion

  //#region Foods

  get craftFoodBuy(): number {
    if (this.craft.craftConfig.getCraftFood(this.craft.jsonReaderService) && this.craft.craftConfig.usedFood) {
      return this.craft.craftFoodBuyRealPriceUnit * this.craft.craftConfig.craftFoodAmount;
    }
    return 0;
  }

  get transportFoodBuy(): number {
    if (this.craft.craftConfig.getTransportFood(this.craft.jsonReaderService) && this.craft.craftConfig.usedFood) {
      return this.craft.transportFoodBuyRealPriceUnit * this.craft.craftConfig.transportFoodAmount;
    }
    return 0;
  }

  get craftFoodSetupFee(): number {
    if ([PRICE_TYPES.buy, PRICE_TYPES.average].includes(this.craft.craftConfig.foodBuyPriceType)) {
      return Math.ceil(this.craftFoodBuy * this.craft.craftConfig.setupFee.value);
    }
    return 0;
  }

  get transportFoodSetupFee(): number {
    if ([PRICE_TYPES.buy, PRICE_TYPES.average].includes(this.craft.craftConfig.foodBuyPriceType)) {
      return Math.ceil(this.transportFoodBuy * this.craft.craftConfig.setupFee.value);
    }
    return 0;
  }

  get totalCraftFoodCost(): number {
    return this.craftFoodBuy + this.craftFoodSetupFee;
  }

  get totalTransportFoodCost(): number {
    return this.transportFoodBuy + this.transportFoodSetupFee;
  }

  get totalFoodCost(): number {
    return this.totalCraftFoodCost + this.totalTransportFoodCost;
  }

  //#region Journals
  get numberOfFilledJournalsAllRuns(): number {
    if (!this.craft.hasJournal || !this.craft.craftConfig.usedJournals) return 0;
    return this.baseFameJournalAllRuns / (this.craft.journalEmpty?.journalMaxFame ?? 1);
  }
  get journalFullSalesTaxAllRuns(): number {
    return Math.ceil(this.journalFullSellAllRuns * this.craft.craftConfig.salesTax.value);
  }
  get journalFullSellSetupFeeAllRuns(): number {
    if ([PRICE_TYPES.sell, PRICE_TYPES.average].includes(this.craft.craftConfig.journalSellPriceType)) {
      return Math.ceil(this.journalFullSellAllRuns * this.craft.craftConfig.setupFee.value);
    }
    return 0;
  }
  get journalFullSellAllRuns(): number {
    return this.numberOfFilledJournalsAllRuns * this.craft.journalFullSellRealPriceUnit;
  }
  get journalEmptyBuyAllRuns(): number {
    return this.numberOfFilledJournalsAllRuns * this.craft.journalEmptyBuyRealPriceUnit;
  }
  get journalEmptyBuySetupFeeAllRuns(): number {
    if ([PRICE_TYPES.buy, PRICE_TYPES.average].includes(this.craft.craftConfig.journalBuyPriceType)) {
      return Math.ceil(this.journalEmptyBuyAllRuns * this.craft.craftConfig.setupFee.value);
    }
    return 0;
  }
  get totalJournalCostAllRuns(): number {
    return this.journalFullSalesTaxAllRuns + this.journalFullSellSetupFeeAllRuns + this.journalEmptyBuyAllRuns + this.journalEmptyBuySetupFeeAllRuns;
  }
  //#endregion
}

export class Ingredient {
  ingredient?: Item;
  ingredientCraftResource: CraftResource;
  recipe: Recipe;
  buyPriceRealUnit: number = 0;
  leftoverAmountReal: number = 0;
  get totalAmountAfterRRRAllRuns(): number {
    return this.recipe.craft.jsonReaderService.getTotalIngredientAmountAfterRRR(
      this.recipe.craft.craftConfig.recipeRuns,
      this.ingredientCraftResource.count,
      this.ingredientCraftResource.maxReturnAmount,
      this.recipe.craft.craftConfig.rrr
    ).amountUsed;
  }
  get leftOverEstimatedAfterRRRAllRuns(): number {
    let result = this.recipe.craft.jsonReaderService.getTotalIngredientAmountAfterRRR(
      this.recipe.craft.craftConfig.recipeRuns,
      this.ingredientCraftResource.count,
      this.ingredientCraftResource.maxReturnAmount,
      this.recipe.craft.craftConfig.rrr
    ).leftOverAmount;
    return result;
  }
  get ingredientMinMaxPrice(): {
    minSellPrice: MarketResponse | undefined;
    maxBuyPrice: MarketResponse | undefined;
  } {
    if (this.ingredientCraftResource.ingredientItem) {
      return this.recipe.craft.aoDataGetterService.getMinAndMaxPrice(
        this.ingredientCraftResource.ingredientItem,
        this.recipe.craft.craftConfig.buyLocation,
        this.recipe.craft.prices,
        undefined
      );
    } else return { minSellPrice: undefined, maxBuyPrice: undefined };
  }
  get ingredientAverage(): { averagePrice: number | undefined; averageAmount: number | undefined } {
    if (this.ingredientCraftResource.ingredientItem) {
      return this.recipe.craft.aoDataGetterService.getAveragePriceAndAmount(
        this.ingredientCraftResource.ingredientItem,
        this.recipe.craft.craftConfig.buyLocation,
        this.recipe.craft.histories,
        undefined,
        this.recipe.craft.craftConfig.averageNumberOfDays
      );
    } else return { averagePrice: 0, averageAmount: 0 };
  }
  get buyPriceUnit(): number {
    switch (this.recipe.craft.craftConfig.buyPriceType) {
      case PRICE_TYPES.average: {
        return this.ingredientAverage.averagePrice ?? 0;
      }
      case PRICE_TYPES.buy: {
        return this.ingredientMinMaxPrice.maxBuyPrice?.buyPriceMax ?? 0;
      }
      case PRICE_TYPES.sell: {
        return this.ingredientMinMaxPrice.minSellPrice?.sellPriceMin ?? 0;
      }
      default: {
        return 0;
      }
    }
  }
  get buyPriceAllRuns(): number {
    return this.totalAmountAfterRRRAllRuns * this.buyPriceRealUnit;
  }
  get setupFeeCostAllRuns(): number {
    if ([PRICE_TYPES.buy, PRICE_TYPES.average].includes(this.recipe.craft.craftConfig.buyPriceType)) {
      return Math.ceil(this.buyPriceAllRuns * this.recipe.craft.craftConfig.setupFee.value);
    }
    return 0;
  }
  get totalCostAllRuns(): number {
    return this.buyPriceAllRuns + this.setupFeeCostAllRuns;
  }
  get leftOverSellAllRuns(): number {
    return Math.floor(this.leftoverAmountReal * this.buyPriceRealUnit);
  }
  get leftOverSetupFeeAllRuns(): number {
    return Math.ceil(this.leftOverSellAllRuns * this.recipe.craft.craftConfig.setupFee.value);
  }
  get leftOverSalesTaxAllRuns(): number {
    return Math.ceil(this.leftOverSellAllRuns * this.recipe.craft.craftConfig.salesTax.value);
  }

  get leftOverResultAllRuns(): number {
    return this.leftOverSellAllRuns - this.leftOverSetupFeeAllRuns - this.leftOverSalesTaxAllRuns;
  }
  get totalWeight(): number {
    return this.totalAmountAfterRRRAllRuns * (this.ingredient?.weight ?? 0);
  }

  resetLeftOverAmountToEstimated() {
    this.leftoverAmountReal = this.leftOverEstimatedAfterRRRAllRuns;
  }
  constructor(_recipe: Recipe, _ingredientCraftResource: CraftResource) {
    this.recipe = _recipe;
    this.ingredientCraftResource = _ingredientCraftResource;
    this.ingredient = this.recipe.craft.jsonReaderService.getItemFromIndex(
      this.recipe.craft.jsonReaderService.getUniqueNameWithAt(this.ingredientCraftResource.uniqueName, this.ingredientCraftResource.enchantmentLevel ?? 0)
    );
    this.resetLeftOverAmountToEstimated();
  }
}
